import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import {
  X,
  TrendingUp,
  TrendingDown,
  Bell,
  ChevronRight,
  ChevronLeft,
  Clock,
  Target,
  Shield,
  ArrowRight,
} from 'lucide-react';
import Button from '../ui/Button';
import Badge from '../ui/Badge';
import { signalsService } from '../../services/signals.service';
import { useSignalStream } from '../../hooks/useSignalStream';
import { useSignalNotificationStore } from '../../store/signalNotificationStore';
import { logger } from '../../lib/logger.js';
import { getSymbolName, type Signal } from '@trading/shared';

interface SignalPopupProps {
  onSignalClick?: (signal: Signal) => void;
}

export default function SignalPopup({ onSignalClick }: SignalPopupProps) {
  const { t } = useTranslation('common');
  const location = useLocation();

  const { signals, addSignal, loadInitial, removeSignal } = useSignalNotificationStore();

  const [currentIndex, setCurrentIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);

  // One initial fetch on mount to populate the carousel with signals the user
  // may have missed while offline. Real-time additions come via useSignalStream.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const newSignals = await signalsService.getNewSignals();
        if (!cancelled && newSignals.length > 0) {
          loadInitial(newSignals);
        }
      } catch (error: unknown) {
        // When the user has hit their plan's signal limit, suppress the popup
        // silently — they will see the upgrade prompt on the Signals page.
        const axiosErr = error as { response?: { status?: number; data?: { code?: string } } };
        if (
          axiosErr?.response?.status === 403 &&
          axiosErr?.response?.data?.code === 'SUB_SIGNALS_LIMIT'
        ) {
          return;
        }
        logger.error('[SignalPopup] Failed to load initial signals:', error);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // intentionally run once on mount

  // Show the popup when signals arrive and the user is not on the signals page.
  useEffect(() => {
    if (signals.length > 0 && !isMinimized && location.pathname !== '/signals') {
      setIsVisible(true);
    }
  }, [signals.length, isMinimized, location.pathname]);

  // Real-time delivery via WebSocket.
  const handleNewSignal = useCallback(
    (signal: Signal) => {
      addSignal(signal);
      if (location.pathname !== '/signals' && !isMinimized) {
        setIsVisible(true);
        setCurrentIndex(0); // jump to the newest signal
      }
    },
    [addSignal, location.pathname, isMinimized],
  );

  useSignalStream({ onNewSignal: handleNewSignal });

  const currentSignal = signals[currentIndex];

  const handleDismiss = useCallback(
    async (signalId: string) => {
      try {
        await signalsService.dismissSignal(signalId);
      } catch {
        // Dismiss locally even if the API call fails.
      }

      removeSignal(signalId);

      // After removal the list is shorter; clamp the index.
      setCurrentIndex((prev) => {
        const nextLen = signals.length - 1;
        return nextLen === 0 ? 0 : Math.min(prev, nextLen - 1);
      });

      if (signals.length <= 1) {
        setIsVisible(false);
      }
    },
    [removeSignal, signals.length],
  );

  const handleDismissAll = useCallback(async () => {
    // Snapshot the list before async iteration mutates state.
    const snapshot = [...signals];
    for (const signal of snapshot) {
      await handleDismiss(signal.id);
    }
  }, [signals, handleDismiss]);

  const handleViewSignal = useCallback(
    (signal: Signal) => {
      if (onSignalClick) {
        onSignalClick(signal);
      }
      void handleDismiss(signal.id);
    },
    [onSignalClick, handleDismiss],
  );

  const handleViewAll = useCallback(() => {
    setIsVisible(false);
    setIsMinimized(true);
  }, []);

  const nextSignal = useCallback(() => {
    setCurrentIndex((prev) => (prev + 1) % signals.length);
  }, [signals.length]);

  const prevSignal = useCallback(() => {
    setCurrentIndex((prev) => (prev - 1 + signals.length) % signals.length);
  }, [signals.length]);

  if (signals.length === 0) return null;

  // Minimized indicator
  if (isMinimized) {
    return (
      <motion.button
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        exit={{ scale: 0 }}
        onClick={() => {
          setIsMinimized(false);
          setIsVisible(true);
        }}
        className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full bg-accent-primary flex items-center justify-center shadow-lg hover:scale-105 transition-transform"
      >
        <Bell className="w-6 h-6 text-white" />
        <span className="absolute -top-1 -right-1 w-5 h-5 bg-loss text-white text-xs font-bold rounded-full flex items-center justify-center">
          {signals.length}
        </span>
      </motion.button>
    );
  }

  return (
    <AnimatePresence>
      {isVisible && currentSignal && (
        <motion.div
          initial={{ opacity: 0, y: 100, scale: 0.9 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 100, scale: 0.9 }}
          className="fixed bottom-6 right-6 z-50 w-full max-w-md"
        >
          <div className="bg-bg-elevated border border-border-subtle rounded-xl shadow-2xl overflow-hidden">
            {/* Header */}
            <div
              className={`px-4 py-3 flex items-center justify-between ${
                currentSignal.direction === 'LONG' ? 'bg-profit/20' : 'bg-loss/20'
              }`}
            >
              <div className="flex items-center gap-2">
                <Bell className="w-5 h-5 text-accent-primary" />
                <span className="font-semibold text-text-primary">New Signal</span>
                {signals.length > 1 && (
                  <Badge variant="default" size="sm">
                    {currentIndex + 1}/{signals.length}
                  </Badge>
                )}
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setIsMinimized(true)}
                  className="p-1 hover:bg-bg-tertiary rounded transition-colors text-text-secondary"
                  title="Minimize"
                >
                  <ChevronRight className="w-5 h-5 rotate-90" />
                </button>
                <button
                  onClick={() => void handleDismiss(currentSignal.id)}
                  className="p-1 hover:bg-bg-tertiary rounded transition-colors text-text-secondary"
                  title="Dismiss"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Signal Content */}
            <div className="p-4 space-y-4">
              {/* Asset & Direction */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div
                    className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                      currentSignal.direction === 'LONG' ? 'bg-profit/20' : 'bg-loss/20'
                    }`}
                  >
                    {currentSignal.direction === 'LONG' ? (
                      <TrendingUp className="w-6 h-6 text-profit" />
                    ) : (
                      <TrendingDown className="w-6 h-6 text-loss" />
                    )}
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-text-primary">{getSymbolName(currentSignal.asset)}</h3>
                    <div className="flex items-center gap-2">
                      <Badge
                        variant={currentSignal.direction === 'LONG' ? 'success' : 'danger'}
                        size="sm"
                      >
                        {currentSignal.direction}
                      </Badge>
                      <span className="text-sm text-text-secondary">{currentSignal.timeframe}</span>
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-sm text-text-secondary">{t('signals.confidence')}</div>
                  <div className="text-lg font-bold text-accent-primary">
                    {currentSignal.confidence}%
                  </div>
                </div>
              </div>

              {/* Price Levels */}
              <div className="grid grid-cols-3 gap-2">
                <div className="bg-bg-tertiary rounded-lg p-2 text-center">
                  <div className="text-xs text-text-muted flex items-center justify-center gap-1">
                    <Target className="w-3 h-3" /> {t('signals.entry')}
                  </div>
                  <div className="font-mono font-bold text-text-primary">
                    {currentSignal.entryPrice}
                  </div>
                </div>
                <div className="bg-bg-tertiary rounded-lg p-2 text-center">
                  <div className="text-xs text-text-muted flex items-center justify-center gap-1">
                    <Shield className="w-3 h-3" /> {t('signals.sl')}
                  </div>
                  <div className="font-mono font-bold text-loss">{currentSignal.stopLoss}</div>
                </div>
                <div className="bg-bg-tertiary rounded-lg p-2 text-center">
                  <div className="text-xs text-text-muted flex items-center justify-center gap-1">
                    <Target className="w-3 h-3" /> {t('signals.tp1')}
                  </div>
                  <div className="font-mono font-bold text-profit">{currentSignal.takeProfit1}</div>
                </div>
              </div>

              {/* R/R and Expiry */}
              <div className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-2">
                  <span className="text-text-secondary">{t('signals.rr')}:</span>
                  <span
                    className={`font-bold ${
                      currentSignal.riskReward != null
                        ? currentSignal.riskReward >= 2 ? 'text-profit' : 'text-warning'
                        : 'text-text-muted'
                    }`}
                  >
                    {currentSignal.riskReward != null ? currentSignal.riskReward.toFixed(2) : '—'}
                  </span>
                </div>
                {currentSignal.expiresAt && (
                  <div className="flex items-center gap-1 text-text-secondary">
                    <Clock className="w-4 h-4" />
                    <span>
                      {t('signals.expires')}:{' '}
                      {new Date(currentSignal.expiresAt).toLocaleTimeString()}
                    </span>
                  </div>
                )}
              </div>

              {/* Narrative */}
              {currentSignal.narrative && (
                <p className="text-sm text-text-secondary line-clamp-2">
                  {currentSignal.narrative}
                </p>
              )}

              {/* Expert commentary */}
              {currentSignal.expertNotes && (
                <div className="rounded-md border-l-2 border-accent-primary bg-bg-tertiary px-3 py-2">
                  <p className="text-[10px] uppercase tracking-wider text-text-muted mb-1">
                    {t('signals.expertNotes.heading')}
                  </p>
                  <p className="text-sm italic text-text-primary line-clamp-3">
                    {currentSignal.expertNotes}
                  </p>
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-2">
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => handleViewSignal(currentSignal)}
                  className="flex-1"
                >
                  View Details
                </Button>
                {signals.length > 1 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => void handleDismissAll()}
                    className="text-text-secondary"
                  >
                    Dismiss All
                  </Button>
                )}
              </div>

              {/* View all signals link */}
              <div className="pt-1 border-t border-border-subtle">
                <Link
                  to="/signals"
                  onClick={handleViewAll}
                  className="flex items-center justify-center gap-1.5 text-sm text-accent-primary hover:text-accent-primary/80 transition-colors py-1"
                >
                  {t('signals.viewAll')}
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>

            {/* Navigation for multiple signals */}
            {signals.length > 1 && (
              <div className="px-4 pb-3 flex items-center justify-between">
                <button
                  onClick={prevSignal}
                  className="p-2 hover:bg-bg-tertiary rounded-lg transition-colors"
                >
                  <ChevronLeft className="w-5 h-5 text-text-secondary" />
                </button>
                <div className="flex gap-1">
                  {signals.map((_, idx) => (
                    <button
                      key={idx}
                      onClick={() => setCurrentIndex(idx)}
                      className={`w-2 h-2 rounded-full transition-colors ${
                        idx === currentIndex ? 'bg-accent-primary' : 'bg-bg-tertiary'
                      }`}
                    />
                  ))}
                </div>
                <button
                  onClick={nextSignal}
                  className="p-2 hover:bg-bg-tertiary rounded-lg transition-colors"
                >
                  <ChevronRight className="w-5 h-5 text-text-secondary" />
                </button>
              </div>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
